import {
  BackBar,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  fullDate,
  IdTag,
  type OperationType,
  type OperationValues,
  present,
  TYPE_LABELS,
  today,
} from '@horof/shared';
import { Alert, App, Button, Result, Skeleton } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  CreateOperationDocument,
  DeleteOperationDocument,
  lastProject,
  OperationDocument,
  phoneLock,
  rememberProject,
  UpdateOperationDocument,
  useCanWrite,
  useFinanceLists,
  withBalances,
} from './finance-data';
import { OperationForm } from './OperationForm';

/**
 * A new income or expense (`/new/credit`, `/new/debit`). The cash box and the
 * project used last are proposed; after saving, back home with a message.
 */
export function NewOperationPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [{ data: lists }] = useFinanceLists();
  const [{ fetching }, create] = useMutation(CreateOperationDocument);
  const [problem, setProblem] = useState<string | null>(null);

  if (!lists) {
    return (
      <div className="page">
        <BackBar to="/" label="الرئيسية" />
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }
  const preset: OperationType | null =
    type === 'credit' || type === 'debit' ? type : null;
  const accounts = present(lists.financialAccounts?.nodes);
  const open = present(lists.projects?.nodes).filter(
    (p) => p.status === 'OPEN',
  );
  const remembered = lastProject();
  const initial: OperationValues = {
    type: preset,
    amount: '',
    date: today(),
    accountId:
      (accounts.find((a) => a.type === 'cash') ?? accounts[0])?.id ?? null,
    projectId:
      open.find((p) => p.id === remembered)?.id ??
      (open.length === 1 ? open[0].id : null),
    categoryId: null,
    counterpartyId: null,
    description: '',
    receiptPhotoId: null,
    needsReview: false,
    reviewNote: '',
    debtId: null,
  };

  return (
    <div className="page">
      <BackBar to="/" label="الرئيسية" />
      <h1 className="member-title">
        {preset ? `${TYPE_LABELS[preset]} جديد` : 'عملية جديدة'}
      </h1>
      {problem && <Alert type="error" showIcon message={problem} />}
      <OperationForm
        initial={initial}
        unsureChoice
        saving={fetching}
        submitLabel="حفظ"
        onSubmit={async (operation) => {
          setProblem(null);
          const result = await create({ operation }, withBalances);
          if (result.error) {
            setProblem(
              describeError(result.error, 'تعذّر الحفظ.', FINANCE_ERRORS),
            );
            return;
          }
          rememberProject(operation.projectId);
          message.success(
            `حُفظ ${TYPE_LABELS[operation.type]}: ${dirhams(operation.amount)}`,
          );
          navigate('/', { replace: true });
        }}
      />
    </div>
  );
}

/** An operation: seen, corrected or deleted when it is a simple one. */
export function OperationPage() {
  const { operationId = '' } = useParams();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const { modal, message } = App.useApp();
  const [{ data, error }] = useQuery({
    query: OperationDocument,
    variables: { id: operationId },
  });
  const [{ fetching: saving }, update] = useMutation(UpdateOperationDocument);
  const [, remove] = useMutation(DeleteOperationDocument);
  const [problem, setProblem] = useState<string | null>(null);

  if (!data) {
    return (
      <div className="page">
        <BackBar to="back" label="رجوع" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل العملية" />
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
      </div>
    );
  }
  const op = data.financialOperation;
  if (!op) {
    return (
      <div className="page">
        <BackBar to="back" label="رجوع" />
        <Result status="404" title="العملية غير موجودة" />
      </div>
    );
  }
  const lock = phoneLock(op);
  const editable = canWrite && !lock;

  async function confirmDelete() {
    if (!op) return;
    const ok = await modal.confirm({
      title: 'حذف العملية',
      content: `${TYPE_LABELS[op.type]} ${dirhams(op.amount)} بتاريخ ${fullDate(op.date)}. لا يمكن التراجع.`,
      okText: 'نعم، احذفها',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const result = await remove({ id: op.id }, withBalances);
    if (result.error) {
      message.error(describeError(result.error, 'تعذّر الحذف.', FINANCE_ERRORS));
      return;
    }
    message.success('حُذفت العملية');
    navigate(-1);
  }

  return (
    <div className="page">
      <BackBar to="back" label="رجوع" />
      <h1 className="member-title">
        {TYPE_LABELS[op.type]}: {dirhams(op.amount)}
      </h1>
      {(op.category || op.counterparty) && (
        <div className="field-hint">
          {[op.category?.name, op.counterparty?.name]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}
      {lock && canWrite && <Alert type="info" showIcon message={lock} />}
      {problem && <Alert type="error" showIcon message={problem} />}
      <OperationForm
        readOnly={!editable}
        initial={{
          type: op.type,
          amount: String(Number(op.amount)),
          date: op.date,
          projectId: op.projectId,
          accountId: op.accountId,
          categoryId: op.categoryId,
          counterpartyId: op.counterpartyId,
          description: op.description ?? '',
          receiptPhotoId: op.receiptPhotoId,
          needsReview: op.needsReview,
          reviewNote: op.reviewNote ?? '',
          debtId: op.debtId,
        }}
        savedOperation={{ type: op.type, amount: op.amount, source: op.source }}
        currentReceipt={op.receiptPhoto}
        saving={saving}
        submitLabel="حفظ التصحيح"
        onSubmit={async (operation) => {
          setProblem(null);
          const result = await update(
            { id: op.id, patch: operation },
            withBalances,
          );
          if (result.error) {
            setProblem(
              describeError(result.error, 'تعذّر الحفظ.', FINANCE_ERRORS),
            );
            return;
          }
          message.success('حُفظ التصحيح');
          navigate(-1);
        }}
      />
      {editable && (
        <Button danger size="large" onClick={confirmDelete}>
          حذف العملية
        </Button>
      )}
      <IdTag id={op.id} label="معرّف العملية" />
    </div>
  );
}
